
// esri.core
import { property, subclass } from "@arcgis/core/core/accessorSupport/decorators";
import promiseUtils from "@arcgis/core/core/promiseUtils";

// esri.widgets
import Widget from "@arcgis/core/widgets/Widget";

// esri.identity
import IdentityManager from "@arcgis/core/identity/IdentityManager";

// EsriGlobalNav
import "esri-global-nav/dist/esri-global-nav.css";
import globalNav from 'esri-global-nav';
import "./GlobalNav.scss";

// DefaultNavStructure
import { constructDefaultNavStructure } from "./DefaultNavStructure";

// notifications
import {
  fetchNotifications,
  deleteNotifications
} from "../../utils/notifications";

// getBaseUrl
import { getBaseUrl } from "../../utils/utils";

// interfaces
import {
  AppsData,
  Attributes,
  ComponentData,
  GlobalNavLink,
  // GlobalNavStructure,
  i18nType,
  NavLinks,
  NavResources
} from "../../../interfaces/interfaces";
import { whenDefinedOnce } from '@arcgis/core/core/watchUtils';

@subclass("GlobalNav")
class GlobalNav extends Widget {
  // ----------------------------------
  //
  //  Properties
  //
  // ----------------------------------
  // appConfig
  @property()
  appConfig: any = null;

  // item
  @property()
  item: any = null;

  @property()
  globalNavMessages: any;

  // portal
  @property()
  portal: any = null;

  @property()
  defaultNavStructure: any;

  // ----------------------------------
  //
  //  Private variables
  //
  // ----------------------------------
  private _navLinks: NavLinks = null;
  private _navResources: NavResources = null;

  // ----------------------------------
  //
  //  Lifecycle methods
  //
  // ----------------------------------
  constructor(params?: any) {
    super(params);
  }

  postInitialize() {
    whenDefinedOnce(this, "globalNavMessages", () => {
      this.defaultNavStructure = constructDefaultNavStructure(this.globalNavMessages);
    });
  }

  // ----------------------------------
  //
  //  Public methods
  //
  // ----------------------------------
  // _createHeader
  async createHeader(headerTitle: string, imgRef: string): Promise<void> {
    whenDefinedOnce(this, "defaultNavStructure", async () => {
      const menuData = await this._getNavStructure();
      // const itemTitle = this?.item?.title;

      const titleNode = document.createElement("header") as any;
      titleNode.classList.add("esri-global-nav__title-node-container");

      const heading = document.createElement("h1");
      heading.classList.add("esri-global-nav__title");
      heading.innerText = `${headerTitle}`;

      const img = document.createElement("img");
      img.src = imgRef;
      img.height = 25;
      img.style.display = "inline";


      titleNode.appendChild(img);
      titleNode.appendChild(heading);

      // Set containing title node to brand.label and brand.brandText to Global Nav
      menuData.header.brand.label = titleNode;
      menuData.header.brand.brandText = titleNode;

      globalNav.createHeader({
        targetElm: ".global-nav",
        menuData
      });
      this._addEventListeners();
    });
  }

  // ----------------------------------
  //
  //  Private methods
  //
  // ----------------------------------

  // getNavStructure
  private async _getNavStructure(): Promise<any> {
    this._navLinks = this._buildNavLinks();
    if (this.portal.user) {
      this._getSignedInNavStructure();
    } else {
      this._getSignedOutNavStructure();
    }
    
    await this._setAccount();
    await this._setNotifications();
    return this.defaultNavStructure;
  }

  // _buildNavLinks
  private _buildNavLinks(): NavLinks {
    const { portal } = this;
    const user = portal.user || {};
    const userRole = user?.userRole;
    const portalHost = getBaseUrl(portal);
    const helpUrl = this._getHelpUrl();
    const show = this._showEsriLinks();
    const esriCommunityUrl = this._getEsriCommunityUrl();
    const myEsriUrl = this._getMyEsriUrl();
    const esriTrainingUrl = this._getEsriTrainingUrl();
    return {
      myProfile: {
        href: `${portalHost}/home/user.html`
      },
      pricing: {
        href: `${portalHost}/features/plans/pricing.html`
      },
      overview: {
        href: `${portalHost}/features/features.html`
      },
      groups: {
        href: `${portalHost}/home/groups.html`
      },
      myContent: {
        href: `${portalHost}/home/content.html`
      },
      help: {
        href: helpUrl,
        openInNewTab: true
      },
      gallery: {
        show: portal.id,
        href: `${portalHost}/home/gallery.html`
      },
      home_link: {
        href: `${portalHost}/home/index.html`
      },
      map: {
        href: `${portalHost}/apps/mapviewer/index.html`
      },
      scene: {
        href: `${portalHost}/home/webscene/viewer.html`
      },
      notebook: {
        show: this.portal.sourceJSON.supportsHostedNotebooks && userRole?.canCreateNotebooks(),
        href: `${portalHost}/home/notebook/notebook.html`
      },
      myAccount: {
        show: this._orgPageRestrictionsDoNotApply() && this._canViewOrgUsers(),
        href: `${portalHost}/home/organization.html`
      },
      esriCommunity: {
        show,
        href: esriCommunityUrl,
        openInNewTab: true
      },
      myEsri: {
        show,
        href: myEsriUrl,
        openInNewTab: true
      },
      training: {
        show,
        href: esriTrainingUrl,
        openInNewTab: true
      }
    };
  }

  // _getHelpUrl
  private _getHelpUrl(): string {
    const { helpBase, isPortal } = this.portal;
    if (isPortal) {
      return this._getPortalHelpUrl();
    } else {
      return "https://doc.arcgis.com/en/instant-apps/latest/get-started/about-instant-apps.htm" || helpBase || "support.html";
    }
  }

  // _getPortalHelpUrl
  private _getPortalHelpUrl(): string {
    const { host, protocol, port } = location;
    const { allSSL, helpBase } = this.portal;
    // go directly to Portal subsection instead of full Enterprise help
    const helpUrl = `${helpBase}portal/`;
    // use secure URL if required - ensure we don't include web adaptor context as helpUrl already includes it,
    // otherwise use whatever protocol they are currently on using a relative URL
    const secureUrl = this._getSecureUrl(`${protocol}//${host}`);
    return allSSL ||
      protocol === "https:" ||
      this._usePortalSSL() ||
      port === "7443"
      ? `${secureUrl}${helpUrl}`
      : helpUrl;
  }

  // _usePortalSSL
  private _usePortalSSL(): boolean {
    const { isMultiTenant, portalHostname, httpPort, httpsPort } = this.portal;
    // returns true if the following four conditions are met:
    // 1: is single tenant
    // 2: has a web adaptor configured (port ':7080/' does not appear in self.portalHostname)
    // 3: user is accessing portal via non-standard ports (user on 7080|7443, so httpPort !== 80 and httpsPort !== 443)
    // 4: user is currently on HTTPS protocol
    // if these conditions are met, it means we want to remain on SSL for the single-tenant portal
    return (
      (isMultiTenant === false || this?.portal?.isPortal) &&
      portalHostname &&
      !portalHostname.match(/:7080\//) &&
      httpPort !== 80 &&
      httpsPort !== 443 &&
      location.protocol === "https:"
    );
  }

  // _getSecureUrl
  private _getSecureUrl(url: string): string {
    let secureUrl = null;
    if (!url) {
      url = getBaseUrl(this.portal);
    }
    const { httpPort, httpsPort, portalMode } = this.portal;
    const { hostname, port } = location;
    if (!portalMode || portalMode !== "singletenant" || !httpsPort) {
      return url.replace("http:", "https:");
    } else {
      if (!port) {
        // default port http port 80
        secureUrl = url.replace(hostname, `${hostname}:${httpsPort}`);
      } else {
        secureUrl = url.replace(port, httpsPort);
        if (this?.portal?.isPortal) {
          // ensure port is switched for portal
          secureUrl = secureUrl.replace(httpPort, httpsPort);
        }
      }
      secureUrl = secureUrl.replace("http:", "https:");
      return secureUrl.replace(/\:80\//, "/").replace(/\:443\//, "/");
    }
  }

  //  _orgPageRestrictionsDoNotApply
  private _orgPageRestrictionsDoNotApply(): boolean {
    const { appConfig, portal } = this;
    const user = portal?.user;
    // -- Missing restrictOrganizationPageToAdmin param
    return appConfig?.restrictOrganizationPageToAdmin
      ? !this.portal.id || (user.role && user.role === "account_admin")
      : true;
  }

  // _canViewOrgUsers
  private _canViewOrgUsers(): boolean {
    return (
      this.portal?.user?.privileges?.indexOf("portal:user:viewOrgUsers") > -1
    );
  }

  // _showEsriLinks
  private _showEsriLinks(): boolean {
    const { isPortal, user } = this.portal;
    return !isPortal && user?.userType === "both";
  }

  // _getEsriCommunityUrl
  private _getEsriCommunityUrl(): string {
    const { appConfig } = this;
    return this._generateEsriLoginHandlerUrl(appConfig.esriCommunityLink);
  }

  // _generateEsriLoginHandlerUrl
  private _generateEsriLoginHandlerUrl(url: string): string {
    const username = this.portal?.user?.username;
    const env = this._getDevEnviroment(),
      devStg = env ? (env === "dev" ? "-dev" : "-stg") : "",
      esriBaseUrl = `https://accounts${devStg}.esri.com/`,
      sharingRest = this._getRestBaseUrl().replace("/apps/sharing/rest/", ""),
      authBaseUrl = `&authBaseUrl=${sharingRest}`,
      esriLoginHandlerUrl = `${esriBaseUrl}LoginHandler/switchuser?`,
      params = `username=${username}&redirect_uri=${url}${authBaseUrl}`;
    return `${esriLoginHandlerUrl}${params}`;
  }

  // _getDevEnviroment
  private _getDevEnviroment(): string {
    const { portalHostname } = this.portal;
    if (portalHostname && portalHostname.indexOf("dev") !== -1) {
      return "dev";
    } else if (portalHostname && portalHostname.indexOf("qa") !== -1) {
      return "qa";
    }
    return null;
  }

  //  _getRestBaseUrl
  private _getRestBaseUrl(): string {
    const { host, protocol } = location;
    const context = this._getHomeContext();
    return `${protocol}//${host}/${context}/sharing/rest/`;
  }

  // _getHomeContext
  private _getHomeContext(): string {
    const pathParts = window.location.pathname.split("/").filter(part => {
      return part;
    });

    const context = pathParts && pathParts.length ? pathParts[0] : "/";
    if (context !== "/" && context !== "home") {
      return `${context}`;
    }
    return context;
  }

  // _getMyEsriUrl
  private _getMyEsriUrl(): string {
    const { appConfig } = this;
    return this._generateEsriLoginHandlerUrl(appConfig.myEsriLink);
  }

  // _getEsriTrainingUrl
  private _getEsriTrainingUrl(): string {
    const url = this._generateEsriTrainingUrl();
    /* jshint +W069 */
    return this._generateEsriLoginHandlerUrl(
      `${window.location.protocol}${url}`
    );
  }

  // _generateEsriTrainingUrl
  private _generateEsriTrainingUrl(): string {
    const esriTrainingMain = "//www.esri.com/training/main/arcgis-online-";
    const esriTraining = `${esriTrainingMain}training`;
    const linkUrls = {
      administrator: `${esriTrainingMain}administrators`,
      publisher: `${esriTrainingMain}publishers`,
      user: `${esriTrainingMain}users`,
      public: esriTraining,
      custom: esriTraining
    };
    if (this.portal?.user?.role) {
      if (this._isAdmin() || this._isBasedOnAdmin()) {
        return linkUrls["administrator"];
      } else if (this._isPublisher() || this._isBasedOnPublisher()) {
        return linkUrls["publisher"];
      } else if (this._isUser() || this._isBasedOnUser()) {
        return linkUrls["user"];
      } else if (this._isCustom()) {
        return linkUrls["custom"];
      }
    } else {
      return linkUrls["public"];
    }
  }

  // _isAdmin
  private _isAdmin(): boolean {
    const { id } = this;
    return (
      this._isDefined(id) && (id === "org_admin" || id === "account_admin")
    );
  }

  // _isBasedOnAdmin
  private _isBasedOnAdmin(): boolean {
    const { role } = this.portal.user;
    return (
      this._isAdmin() ||
      (this._isDefined(role) &&
        (role === "org_admin" || role === "account_admin"))
    );
  }

  // _isPublisher
  private _isPublisher(): boolean {
    const { id } = this;
    return (
      this._isDefined(id) &&
      (id === "org_publisher" || id === "account_publisher")
    );
  }

  // _isBasedOnPublisher
  private _isBasedOnPublisher(): boolean {
    const { role } = this.portal.user;
    return (
      this._isPublisher() ||
      (this._isDefined(role) &&
        (role === "org_publisher" || role === "account_publisher"))
    );
  }

  // _isUser
  private _isUser(): boolean {
    const { id } = this;
    return this._isDefined(id) && (id === "org_user" || id === "account_user");
  }

  // _isBasedOnUser
  private _isBasedOnUser(): boolean {
    const { role } = this.portal.user;
    return (
      this._isUser() ||
      (this._isDefined(role) &&
        (role === "org_user" || role === "account_user"))
    );
  }

  // isCustom
  private _isCustom(): boolean {
    const { id } = this;
    return (
      !this._isUser() &&
      !this._isPublisher() &&
      !this._isAdmin() &&
      this._isDefined(id) &&
      id.length > 0
    );
  }

  // _isDefined
  private _isDefined(value: string): boolean {
    return value !== undefined && value !== null;
  }

  // _getSignedInNavStructure
  private _getSignedInNavStructure(): any {
    const menuLinks = this._isPublicAccount()
      ? [
        "home_link",
        "overview",
        "pricing",
        "map",
        "scene",
        "groups",
        "myContent"
      ]
      : [
        "home_link",
        "gallery",
        "map",
        "scene",
        "notebook",
        "groups",
        "myContent",
        "myAccount"
      ];
    const { defaultNavStructure } = this;
    defaultNavStructure.header.menus = [
      this._filterAndCreateLinks(menuLinks, this.globalNavMessages.globalNav.links)
    ];
    return defaultNavStructure;
  }

  // _isPublicAccount
  private _isPublicAccount(): boolean {
    const { urlKey, isPortal, user } = this.portal;
    return !urlKey && !isPortal && user ? true : false;
  }

  // _getSignedOutNavStructure
  private _getSignedOutNavStructure(): any {
    const isPortal = !this._showProductLinks();
    const isOrgUrl = this.portal.orgUrlKey;
    let menuLinks = ["ArcGIS", "overview", "pricing", "scene", "help"];
    if (!isPortal && isOrgUrl) {
      menuLinks = ["home_link", "gallery", "map", "scene", "help"];
    } else if (isPortal) {
      menuLinks = ["home_link", "gallery", "map", "scene", "groups"];
    }

    this.defaultNavStructure.header.menus = [
      this._filterAndCreateLinks(menuLinks, this.globalNavMessages.globalNav.links)
    ];
    return this.defaultNavStructure;
  }

  // _showProductLinks
  private _showProductLinks(): boolean {
    const { id, access, isPortal } = this.portal;
    return !id && !access && !isPortal;
  }

  // _filterAndCreateLinks
  private _filterAndCreateLinks(
    links: string[],
    i18n: i18nType
  ): GlobalNavLink[] {
    const linkArr = [];
    links.forEach(link => {
      const attributes = this._navLinks[link];
      if (attributes && (!("show" in attributes) || attributes.show)) {
        const linkValue = link === "map" ? i18n[link] : i18n[link];
        linkArr.push(this._createGlobalNavLink(linkValue || link, attributes));
      }
    }, this);
    return linkArr;
  }

  // _createGlobalNavLink
  private _createGlobalNavLink(
    label: string,
    attributes: Attributes
  ): GlobalNavLink {
    return this._getGlobalNavLink(label, attributes);
  }

  // _getGlobalNavLink
  private _getGlobalNavLink(
    label: string,
    attributes: Attributes
  ): GlobalNavLink {
    const { href, data, active, openInNewTab } = attributes;
    return {
      label,
      href: href
        ? typeof href === "function"
          ? attributes.href()
          : href
        : "#",
      data,
      active,
      newContext: openInNewTab
    };
  }

  // _setAccount
  private async _setAccount(): Promise<void> {
    const { portal, defaultNavStructure } = this;
    const portalUser = portal.user;
    const { account } = defaultNavStructure.header;
    if (portalUser) {
      await this._generateGlobalNavResources();
      const { fullName, username } = portalUser;
      const { pathname } = window.location;
      const distPath = pathname.substring(0, pathname.lastIndexOf("/"));
      const userStructure = {
        name: fullName,
        id: username,
        group: "",
        image:
          portalUser.getThumbnailUrl() ||
          `${distPath}/assets/images/no-user-thumb.jpg`
      };
      account.user = userStructure;

      defaultNavStructure.header.account.menus = this._filterAndCreateLinks(
        ["myProfile", "myEsri", "training", "esriCommunity", "help"],
        this.globalNavMessages.globalNav.links
      );
    } else {
      delete account.user;
    }
  }

  // _setNotifications
  private _setNotifications(): Promise<any> {
    const { portal, defaultNavStructure } = this;
    const { user } = portal;

    if (user) {
      this._listenForDismissNotification();
      return fetchNotifications(portal, this.globalNavMessages).then(results => {
        const notifications = defaultNavStructure.header.notifications;
        notifications.messages = results;
        return results;
      });
    } else {
      delete defaultNavStructure.header.notifications;
      return Promise.resolve([]);
    }
  }

  // _listenForDismissNotification
  private _listenForDismissNotification(): void {
    document.addEventListener(
      "header:click:notifications:dismiss",
      (event: CustomEvent) => {
        const { portalUrl, user } = this.portal;
        deleteNotifications(portalUrl, user, event.detail).then(removed => {
          const removedIds = removed.reduce((validNotifications, result) => {
            if (result?.value?.data?.success) {
              validNotifications.push(result.value.data.notificationId);
            }
            return validNotifications;
          }, []);

          this._updateNotificationsComponent(
            (
              this.defaultNavStructure.header.notifications.messages || []
            ).filter(notification => removedIds.indexOf(notification.id) < 0)
          );
        });
      }
    );
  }

  // _updateNotificationsComponent
  private _updateNotificationsComponent(messages: string[]): void {
    this.defaultNavStructure.header.notifications.messages = messages;
    this._updateComponent(
      "header:update:notifications",
      document.querySelector(".esri-header-notifications"),
      this._getDefaultNotifications(messages)
    );
    // if that was the last notification, close the menu
    if (messages.length === 0) {
      this._updateComponent(
        "header:menu:close",
        document.querySelector(".esri-header-notifications"),
        null
      );
    }
  }

  // _updateComponent
  private _updateComponent(
    componentEvent: string,
    componentElement: HTMLElement,
    componentData: ComponentData
  ): void {
    const event = document.createEvent("CustomEvent");
    event.initCustomEvent(componentEvent, true, true, componentData);
    componentElement.dispatchEvent(event);
  }

  // _getDefaultNotifications
  private _getDefaultNotifications(messages: string[]): any {
    return {
      label: this.globalNavMessages.notifications.label,
      dismissAllLabel: this.globalNavMessages.notifications.dismissAllLabel,
      dismissLabel: this.globalNavMessages.notifications.dismissLabel,
      emptyMessage: {
        image: {
          path: [
            "M15.5 1A14.5 14.5 0 1 0 30 15.5 14.5 14.5 0 0 0 15.5 1zm0 28.1a13.6 13.6 0 1 1 13.6-13.6 13.615 13.615 0 0 1-13.6 13.6zM8.581 17.276l.637-.636 3.288 3.098 10.073-9.92.637.637-10.71 10.556z"
          ],
          viewBox: "0 0 32 32"
        },
        text: this.globalNavMessages.notifications.noNotifications
      },
      messages: messages || []
    };
  }

  // _generateGlobalNavResources
  private async _generateGlobalNavResources(): Promise<void> {
    if (!this._canAccessAppLauncher()) {
      return;
    }
    this.portal.user.credential = await IdentityManager.checkSignInStatus(getBaseUrl(this.portal) + "/sharing");
    this.portal.portalUrl = this._getPortalRestUrl();

    this.defaultNavStructure.header.appSwitcher = {
      portal: this.portal,
      user: this.portal.user,
      api: 4
    };

    return;
  }

  // _canAccessAppLauncher
  private _canAccessAppLauncher(): boolean {
    // -- Currently missing subscriptionInfo -- needed to mimick the Home App logic for app launcher access
    // (this.portal.isPortal || (this.portal.subscriptionInfo && this.portal.subscriptionInfo.state === "active"))
    return this.portal?.user?.orgId ? true : false;
  }

  // _appsJson
  private _appsJson(appsData: ComponentData): AppsData {
    return {
      text: {
        dragAndDropIntroText: this.globalNavMessages.globalNav.appLauncher.dragAndDropIntroText,
        gotIt: this.globalNavMessages.globalNav.appLauncher.gotIt,
        showMore: this.globalNavMessages.globalNav.appLauncher.showMore,
        dragAppsHere: this.globalNavMessages.globalNav.appLauncher.dragAppsHere
      },
      label: this.globalNavMessages.globalNav.appLauncher.label,
      disableDragDrop: false,
      loading: !appsData,
      ...appsData
    }
  }

  // _getEsriAuthCookie
  private _getEsriAuthCookie(): AppsData {
    const cookie = this._getCookie("esri_auth");
    return cookie ? JSON.parse(cookie) : {};
  }

  // _getCookie
  private _getCookie(cname: string): string {
    const name = `${cname}=`;
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(";");
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === " ") {
        c = c.substring(1);
      }
      if (c.indexOf(name) === 0) {
        return c.substring(name.length, c.length);
      }
    }
    return "";
  }

  // _getPortalRestUrl
  private _getPortalRestUrl(): string {
    const { customBaseUrl, portalHostname, urlKey } = this.portal;
    const portalHost = urlKey ? `${urlKey}.${customBaseUrl}` : portalHostname;
    return `https://${portalHost}/sharing/rest/`;
  }

  // _getEnvironment
  private _getEnvironment(): string {
    const env = location.href.split(".")[1]?.replace("maps", "");
    return env || "production";
  }

  // _getApps
  private _getApps(connection: any): void {
    if (connection.success) {
      this._navResources.performAction(
        "AppLauncher",
        "getApps",
        { noStrings: true },
        this._createAppLauncher.bind(this)
      );
    }
  }

  // _createAppLauncher
  private _createAppLauncher(appsData: ComponentData): void {
    if (!appsData?.primary?.length && !appsData?.secondary?.length) {
      return;
    }
    this._updateAppLauncherComponent(appsData);
    // Listen for Drag & Drop Changes and Save New App Order
    document
      .querySelector(".esri-header-barrier")
      .addEventListener("header:apps:reorder", (data: ComponentData) => {
        this._navResources.performAction(
          "AppLauncher",
          "setApps",
          data.detail.icons,
          () => ({})
        );
      });
  }

  // _updateAppLauncherComponent
  private _updateAppLauncherComponent(data: ComponentData): void {
    this._updateComponent(
      "header:update:apps",
      document.querySelector(".esri-header-apps"),
      this._appsJson(data)
    );
  }

  // _addEventListeners
  private _addEventListeners(): void {
    const esriHeader = document.querySelector(".esri-header-barrier");
    esriHeader.addEventListener("header:click:signin", () => {
      IdentityManager.getCredential(this.portal.url);
    });

    esriHeader.addEventListener(
      "header:click:signout",
      this._logoutOfAccount.bind(this)
    );
    esriHeader.addEventListener(
      "header:click:switch",
      this._openSwitchAccounts.bind(this)
    );
    esriHeader.addEventListener(
      "header::title:save",
      this._updateTitle.bind(this)
    );
  }

  // _logoutOfAccount
  private async _logoutOfAccount(event: Event): Promise<void> {
    event.preventDefault();
    if (this.portal.isPortal) {
      try {
        // IdentityManager.destroyCredentials() isn't working to sign out 
        // before the redirect, so this attempts to hit the revokeToken 
        // end point directly. If it fails then try destroyCredentials
        const formData = new FormData();
        formData.append("auth_token", this.portal.user.credential.token);
        // Making big assumption here that client_id for Enterprise is always "arcgisonline". 
        // Can't find client_id dynamically through the IdentityManager
        formData.append("client_id", "arcgisonline"); 
        formData.append("token_type_hint", "refresh_token");
        formData.append("f", "json");
  
        const res = await fetch(`${getBaseUrl(this.portal)}/sharing/rest/oauth2/revokeToken`, {
          method: "POST",
          body: formData
        });
        if(!res.ok){
          const err = await res.json();
          throw new Error(err);
        }

      } catch(err) {
        console.error(err);
        IdentityManager.destroyCredentials();
      }
    }
    this._agolLogOut();
  }

  // _agolLogOut
  private _agolLogOut(): void {
    const portalHost = getBaseUrl(this.portal);
    const signOutUrl = `${portalHost}/home/pages/Account/manage_accounts.html`;
    window.location.href = `${signOutUrl}#client_id=arcgisonline&signout=true&redirect_uri=${portalHost}`;
  }

  // openSwitchAccounts
  private _openSwitchAccounts(): void {
    const { href } = window.location;
    const redirectUri = href.replace("index", "account-switcher-callback");
    const portalHost = getBaseUrl(this.portal);
    const myWindow = window.open(
      `${portalHost}/home/pages/Account/manage_accounts.html#client_id=arcgisonline&redirect_uri=${redirectUri}`,
      "_blank"
    );
    myWindow.focus();
  }

  // _updateTitle
  private _updateTitle(result: CustomEvent): void {
    const { item } = this;
    const event = document.createEvent("CustomEvent");
    const dfd = Promise.resolve(true);
    dfd.then(() => {
      event.initCustomEvent("header:update:inlineTitle", true, true, {
        label: result.detail,
        brandText: result.detail
      });
      document.querySelector(".esri-header-inline-title").dispatchEvent(event);
      document.querySelector(".js-page-title").textContent = result.detail;
      if (item) {
        item.set("title", result.detail);
      }
    });
  }
}

export default GlobalNav;
